"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.InputStack = void 0;
var _react = _interopRequireWildcard(require("react"));
require("./InputStack.css");
var _useInputVariant = require("@cbhq/cds-common/hooks/useInputVariant");
var _tokens = require("@cbhq/cds-common/motion/tokens");
var _input = require("@cbhq/cds-common/tokens/input");
var _interactable = require("@cbhq/cds-common/tokens/interactable");
var _usePalette = require("../hooks/usePalette");
var _HStack = require("../layout/HStack");
var _VStack = require("../layout/VStack");
var _ColorSurge = require("../motion/ColorSurge");
var _Interactable = require("../system/Interactable");
var _tokens2 = require("../tokens");
var _linaria = require("../utils/linaria");
var _InputLabel = require("./InputLabel");
const _excluded = ["width", "prependNode", "endNode", "appendNode", "startNode", "disabled", "inputNode", "helperTextNode", "borderWidth", "variant", "labelNode", "testID", "focused", "borderRadius", "height", "disableFocusedStyle", "enableColorSurge"];
/* eslint-disable react/jsx-no-useless-fragment */
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
const inputBaseAreaStyles = "cds-inputBaseAreaStyles-i12wqc8";

// Fixes a problem found in Accordian children element.
// When `overflow: auto` is set the thickened border when focused is not accounted for
// hence you see a cutoff.
// Fix was to add this so there is always 2px outer layer space
const inputAreaContainerStyles = "cds-inputAreaContainerStyles-i1sndg40";

/** this should only be used as a last resort, when focus styles need to be persisted. eg: when a Select PopoverMenu is opened */
const persistedFocusStyles = "cds-persistedFocusStyles-p7719hj";
const InputStack = /*#__PURE__*/(0, _react.memo)( /*#__PURE__*/(0, _react.forwardRef)(function InputStack(_ref, ref) {
  let _ref$width = _ref.width,
    width = _ref$width === void 0 ? '100%' : _ref$width,
    prependNode = _ref.prependNode,
    endNode = _ref.endNode,
    appendNode = _ref.appendNode,
    startNode = _ref.startNode,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    inputNode = _ref.inputNode,
    helperTextNode = _ref.helperTextNode,
    _ref$borderWidth = _ref.borderWidth,
    borderWidth = _ref$borderWidth === void 0 ? 'input' : _ref$borderWidth,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'foregroundMuted' : _ref$variant,
    labelNode = _ref.labelNode,
    _ref$testID = _ref.testID,
    testID = _ref$testID === void 0 ? '' : _ref$testID,
    _ref$focused = _ref.focused,
    focused = _ref$focused === void 0 ? false : _ref$focused,
    _ref$borderRadius = _ref.borderRadius,
    borderRadius = _ref$borderRadius === void 0 ? 'rounded' : _ref$borderRadius,
    height = _ref.height,
    _ref$disableFocusedSt = _ref.disableFocusedStyle,
    disableFocusedStyle = _ref$disableFocusedSt === void 0 ? false : _ref$disableFocusedSt,
    enableColorSurge = _ref.enableColorSurge,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const palette = (0, _usePalette.usePalette)();
  const focusedVariant = (0, _useInputVariant.useInputVariant)(focused, variant);

  // Styling
  const inputBorderRadius = (0, _react.useMemo)(() => {
    return Object.assign({}, prependNode ? {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0
    } : {}, appendNode ? {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0
    } : {});
  }, [prependNode, appendNode]);
  const borderColorFocused = (0, _react.useMemo)(() => {
    if (disableFocusedStyle) {
      return 'transparent';
    }
    if (variant !== 'positive' && variant !== 'negative') {
      return palette.primary;
    }
    return palette[variant];
  }, [disableFocusedStyle, palette, variant]);
  const defaultBorderStyles = (0, _react.useMemo)(() => {
    return Object.assign({
      '--border-color-unfocused': variant === 'secondary' ? 'transparent' : palette[variant === 'foregroundMuted' || !variant ? 'lineHeavy' : variant],
      '--border-color-focused': borderColorFocused,
      '--border-width-focused': _tokens2.borderWidth[borderWidth]
    }, inputBorderRadius);
  }, [variant, palette, borderColorFocused, borderWidth, inputBorderRadius]);
  return /*#__PURE__*/_react.default.createElement(_VStack.VStack, _extends({
    gap: _input.inputStackGap,
    opacity: disabled ? _interactable.accessibleOpacityDisabled : 1,
    testID: testID,
    width: width
  }, props), !!labelNode && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, typeof labelNode === 'string' ? /*#__PURE__*/_react.default.createElement(_InputLabel.InputLabel, null, labelNode) : labelNode), /*#__PURE__*/_react.default.createElement(_HStack.HStack, null, !!prependNode && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, prependNode), /*#__PURE__*/_react.default.createElement("div", {
    className: inputAreaContainerStyles
  }, /*#__PURE__*/_react.default.createElement(_Interactable.Interactable, {
    ref: ref,
    as: "span",
    background: variant === 'secondary' ? 'secondary' : 'background',
    borderRadius: borderRadius,
    borderWidth: borderWidth,
    className: (0, _linaria.cx)(inputBaseAreaStyles, focused && persistedFocusStyles),
    disabled: disabled,
    height: height,
    style: defaultBorderStyles,
    testID: "input-interactable-area"
  }, focused && enableColorSurge && /*#__PURE__*/_react.default.createElement(_ColorSurge.ColorSurge, {
    background: focusedVariant
  }), !!startNode && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, startNode), inputNode, !!endNode && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, endNode))), !!appendNode && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, appendNode)), !!helperTextNode && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, helperTextNode));
}));
exports.InputStack = InputStack;